/**
 * @fileoverview Type definitions for the Address Extension module.
 *
 * This module extends VirtoCommerce addresses with additional fields
 * required for Arad/HoReCa business requirements.
 */

/**
 * Person type enum for distinguishing natural (individual) vs legal (company) entities.
 */
export enum PersonType {
  /** Individual/Physical person (حقیقی) */
  Natural = "natural",
  /** Company/Legal entity (حقوقی) */
  Legal = "legal",
}

/**
 * Address source identifying where the address originates from.
 */
export enum AddressSource {
  /** Member address from account address book */
  Member = "Member",
  /** Cart address used during checkout */
  Cart = "Cart",
  /** Order address from completed orders */
  Order = "Order",
}

/**
 * Private asset types supported by the system.
 */
export enum PrivateAssetType {
  /** National ID card image (کارت ملی) */
  NationalCard = "NationalCard",
  /** Business license document (جواز کسب) */
  BusinessLicense = "BusinessLicense",
}

/**
 * Address extension data model matching the backend API.
 */
export interface IAddressExtension {
  /** Unique identifier */
  id: string;
  /** ID of the linked address */
  addressId: string;
  /** Source of the address (Member, Cart, Order) */
  addressSource: AddressSource;
  /** Guild/business category display text */
  guild?: string;
  /** Name of the store/business */
  storeName?: string;
  /** GPS latitude coordinate */
  latitude?: number;
  /** GPS longitude coordinate */
  longitude?: number;
  /** Person type: natural or legal */
  personType?: PersonType | null;
  /** Natural person first name */
  naturalPersonName?: string;
  /** Natural person last name */
  naturalPersonFamily?: string;
  /** Natural person national code (کد ملی) - 10 digits */
  naturalPersonNationalCode?: string;
  /** ID of the national card asset */
  nationalCardAssetId?: string;
  /** Legal person/company name */
  legalPersonName?: string;
  /** Legal person national code (شناسه ملی) - 11 digits */
  legalPersonNationalCode?: string;
  /** Legal person economic code (کد اقتصادی) - 12 digits */
  legalPersonEconomicalCode?: string;
  /** ID of the business license asset */
  businessLicenseAssetId?: string;
  /** Creation timestamp */
  createdDate?: string;
  /** Last modification timestamp */
  modifiedDate?: string;
  /** Creator user ID */
  createdBy?: string;
  /** Last modifier user ID */
  modifiedBy?: string;
}

/**
 * Input type for creating/updating address extensions.
 */
export interface IAddressExtensionInput {
  /** ID of the linked address */
  addressId: string;
  /** Source of the address */
  addressSource: AddressSource;
  /** Guild/business category */
  guild?: string;
  /** Store/business name */
  storeName?: string;
  /** GPS latitude */
  latitude?: number;
  /** GPS longitude */
  longitude?: number;
  /** Person type */
  personType?: PersonType | null;
  /** Natural person first name */
  naturalPersonName?: string;
  /** Natural person last name */
  naturalPersonFamily?: string;
  /** Natural person national code */
  naturalPersonNationalCode?: string;
  /** National card asset ID */
  nationalCardAssetId?: string;
  /** Legal person name */
  legalPersonName?: string;
  /** Legal person national code */
  legalPersonNationalCode?: string;
  /** Legal person economic code */
  legalPersonEconomicalCode?: string;
  /** Business license asset ID */
  businessLicenseAssetId?: string;
}

/**
 * Search criteria for querying address extensions.
 */
export interface IAddressExtensionSearchCriteria {
  /** Filter by address IDs */
  addressIds?: string[];
  /** Filter by address source */
  addressSource?: AddressSource;
  /** Filter by person type */
  personType?: PersonType;
  /** Search keyword */
  keyword?: string;
  /** Number of records to skip */
  skip?: number;
  /** Maximum number of records to return */
  take?: number;
}

/**
 * Search result for address extensions.
 */
export interface IAddressExtensionSearchResult {
  /** Total count of matching records */
  totalCount: number;
  /** Array of matching address extensions */
  results: IAddressExtension[];
}

/**
 * Validation result for address extension.
 */
export interface IAddressExtensionValidationResult {
  /** Whether the extension is valid for the given context */
  isValid: boolean;
  /** Array of validation error messages */
  errors: string[];
  /** Field-specific validation errors */
  fieldErrors?: Record<string, string[]>;
}

/**
 * Validation context specifying where the address will be used.
 */
export interface IAddressExtensionValidationContext {
  /** Address ID to validate */
  addressId: string;
  /** Address source */
  addressSource: AddressSource;
  /** Whether validating for shipping context */
  isShipping: boolean;
  /** Whether validating for billing context */
  isBilling: boolean;
}

/**
 * Private asset metadata.
 */
export interface IPrivateAsset {
  /** Unique identifier */
  id: string;
  /** Owner user/contact ID */
  ownerId: string;
  /** Owner type (Contact, User) */
  ownerType: string;
  /** Asset type */
  assetType: PrivateAssetType;
  /** Original file name */
  fileName: string;
  /** MIME content type */
  contentType: string;
  /** File size in bytes */
  fileSize: number;
  /** Creation timestamp */
  createdDate: string;
  /** Creator user ID */
  createdBy: string;
}

/**
 * Private asset response with signed URL for access.
 */
export interface IPrivateAssetResponse extends IPrivateAsset {
  /** Signed URL for accessing the file (time-limited) */
  signedUrl?: string;
  /** Expiration time of the signed URL */
  signedUrlExpiration?: string;
}

/**
 * Upload result for private assets.
 */
export interface IPrivateAssetUploadResult {
  /** Uploaded asset metadata */
  asset: IPrivateAsset;
  /** Whether the upload was successful */
  success: boolean;
  /** Error message if upload failed */
  error?: string;
}

/**
 * Guild option from the backend.
 */
export interface IGuildOption {
  /** Guild identifier (from API: id) */
  value: string;
  /** Display label (from API: name) */
  label: string;
}

/**
 * Raw guild option response from API.
 */
export interface IGuildApiResponse {
  /** Guild identifier */
  id: string;
  /** Display name */
  name: string;
}

/**
 * Geolocation coordinates.
 */
export interface IGeoLocation {
  /** Latitude coordinate (-90 to 90) */
  latitude: number;
  /** Longitude coordinate (-180 to 180) */
  longitude: number;
}

/**
 * Extended address type combining base address with extension fields.
 * Used for UI components that need both sets of data.
 */
export interface IExtendedAddress {
  /** Base address ID */
  addressId: string;
  /** Address source */
  addressSource: AddressSource;
  /** Extension data (may be null if not yet created) */
  extension: IAddressExtension | null;
}

/**
 * Form state for address extension fields.
 */
export interface IAddressExtensionFormState {
  /** Guild/business category */
  guild: string;
  /** Store name */
  storeName: string;
  /** Latitude coordinate */
  latitude: number | null;
  /** Longitude coordinate */
  longitude: number | null;
  /** Person type selection */
  personType: PersonType | null;
  /** Natural person first name */
  naturalPersonName: string;
  /** Natural person family name */
  naturalPersonFamily: string;
  /** Natural person national code */
  naturalPersonNationalCode: string;
  /** National card asset ID */
  nationalCardAssetId: string | null;
  /** Legal person name */
  legalPersonName: string;
  /** Legal person national code */
  legalPersonNationalCode: string;
  /** Legal person economic code */
  legalPersonEconomicalCode: string;
  /** Business license asset ID */
  businessLicenseAssetId: string | null;
}

/**
 * Address purpose context for conditional field display/validation.
 */
export type AddressPurposeType = "shipping" | "billing" | "general";

/**
 * Configuration for address extension module.
 */
export interface IAddressExtensionConfig {
  /** Default map center latitude (Tehran) */
  defaultMapLatitude: number;
  /** Default map center longitude (Tehran) */
  defaultMapLongitude: number;
  /** Maximum file size for uploads in bytes */
  maxAssetSizeBytes: number;
  /** Allowed MIME types for uploads */
  allowedAssetTypes: string[];
}

// Legacy type aliases for backward compatibility (with required Type suffix)
/** @deprecated Use IAddressExtension instead */
export type AddressExtensionType = IAddressExtension;
/** @deprecated Use IAddressExtensionInput instead */
export type AddressExtensionInputType = IAddressExtensionInput;
/** @deprecated Use IAddressExtensionSearchCriteria instead */
export type AddressExtensionSearchCriteriaType = IAddressExtensionSearchCriteria;
/** @deprecated Use IAddressExtensionSearchResult instead */
export type AddressExtensionSearchResultType = IAddressExtensionSearchResult;
/** @deprecated Use IAddressExtensionValidationResult instead */
export type AddressExtensionValidationResultType = IAddressExtensionValidationResult;
/** @deprecated Use IAddressExtensionValidationContext instead */
export type AddressExtensionValidationContextType = IAddressExtensionValidationContext;
/** @deprecated Use IPrivateAsset instead */
export type PrivateAssetDataType = IPrivateAsset;
/** @deprecated Use IPrivateAssetResponse instead */
export type PrivateAssetResponseType = IPrivateAssetResponse;
/** @deprecated Use IPrivateAssetUploadResult instead */
export type PrivateAssetUploadResultType = IPrivateAssetUploadResult;
/** @deprecated Use IGuildOption instead */
export type GuildOptionType = IGuildOption;
/** @deprecated Use IGeoLocation instead */
export type GeoLocationType = IGeoLocation;
/** @deprecated Use IExtendedAddress instead */
export type ExtendedAddressType = IExtendedAddress;
/** @deprecated Use IAddressExtensionFormState instead */
export type AddressExtensionFormStateType = IAddressExtensionFormState;
/** @deprecated Use IAddressExtensionConfig instead */
export type AddressExtensionConfigType = IAddressExtensionConfig;
